import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Container,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip as MuiTooltip,
} from '@mui/material';
import {
  SportsHandball as BadmintonIcon,
  SportsCricket as CricketIcon,
  SportsTennis as TennisIcon,
  SportsSoccer as FootballIcon,
  Sports as AthleticsIcon,
  ArrowForward as ArrowForwardIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Assessment as AssessmentIcon,
  EmojiEvents as RankingIcon,
  School as LearningIcon,
  WorkspacePremium as CertificateIcon,
  Description as DescriptionIcon,
  Compare as CompareIcon,
  AutoAwesome as RecommendationsIcon,
  Timeline as TimelineIcon,
  ExitToApp as LogoutIcon,
  InfoOutlined as InfoIcon,
  TipsAndUpdatesOutlined as TipIcon,
  WarningAmberOutlined as WarningIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@store/authStore';
import EncycdataLogo from '@components/layout/EncycdataLogo';
import {
  getPortalPaths,
  isCoachLearningCoursesPath,
  isCoachLearningRoadmapPath,
  isPlayerLearningCoursesPath,
  isPlayerLearningRoadmapPath,
  type PortalMode,
} from '@utils/portalPaths';
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LabelList,
} from 'recharts';

interface SportCategory {
  sport: string;
  categories: Array<{
    category: string;
    displayName: string;
    skillCount: number;
  }>;
}

interface RoadmapPoint {
  month: number; // base month in 60-month reference timeline
  week: number; // base week in 260-week reference timeline
  y: number;
  phase: 'L1' | 'L2' | 'L3' | 'L4';
  category: 'Skills' | 'Drills' | 'Fitness';
  code: string;
  name: string;
  courtPosition?: string;
  halfSide?: string;
  strokeType?: string;
  strokeAction?: string;
  shotType?: string;
  placement?: string;
  startMonth?: number;
  endMonth?: number;
  startLevel?: 'L1' | 'L2' | 'L3' | 'L4';
  practiceCadence?: string;
}
interface RoadmapSeries {
  skills: RoadmapPoint[];
  drills: RoadmapPoint[];
  fitness: RoadmapPoint[];
}

interface RoadmapPlotPoint extends RoadmapPoint {
  quarter: number;
  x: number;
  actionLabel: string;
  actionLabelShort: string;
  actionLabelWrapped: string;
  yPlot?: number;
}

const DRAWER_WIDTH = 280;
const DIFFICULTY_TEXT: Record<number, string> = {
  1: 'Very Easy',
  2: 'Easy',
  3: 'Moderate',
  4: 'Hard',
  5: 'Very Hard',
};
const ROADMAP_TOTAL_MONTHS = 60;
const ROADMAP_TOTAL_WEEKS = 260;
const ROADMAP_LEVEL_RANGES: Record<'L1' | 'L2' | 'L3' | 'L4', { start: number; end: number; label: string }> = {
  L1: { start: 1, end: 12, label: 'Beginner' },
  L2: { start: 13, end: 36, label: 'Intermediate' },
  L3: { start: 37, end: 54, label: 'Advanced' },
  L4: { start: 55, end: 60, label: 'Pro' },
};
const wrapLabel = (text: string, maxChars = 26, maxLines = 3) => {
  const parts = String(text).split(' ');
  const lines: string[] = [];
  let current = '';
  for (const part of parts) {
    const candidate = current ? `${current} ${part}` : part;
    if (candidate.length <= maxChars) {
      current = candidate;
      continue;
    }
    if (current) lines.push(current);
    current = part;
    if (lines.length === maxLines - 1) break;
  }
  if (lines.length < maxLines && current) lines.push(current);
  if (lines.length === maxLines && parts.join(' ').length > lines.join(' ').length) {
    lines[maxLines - 1] = `${lines[maxLines - 1].slice(0, Math.max(0, maxChars - 3))}...`;
  }
  return lines.join('\n');
};

interface LearningMainPageProps {
  portalMode?: PortalMode;
}

const LearningMainPage: React.FC<LearningMainPageProps> = ({ portalMode = 'player' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuthStore();
  const paths = getPortalPaths(portalMode);
  const hideSidebar = portalMode === 'coach';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sportCategories, setSportCategories] = useState<SportCategory[]>([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [roadmapSport, setRoadmapSport] = useState<string>('Badminton');
  const [roadmapLevel, setRoadmapLevel] = useState<'ALL' | 'L1' | 'L2' | 'L3' | 'L4'>('L1');
  const [roadmapTab, setRoadmapTab] = useState<'ALL' | 'skills' | 'drills' | 'fitness'>('ALL');
  const [roadmapChartType, setRoadmapChartType] = useState<'scatter' | 'gantt'>('gantt');
  const [playerAge, setPlayerAge] = useState<number | null>(null);
  const [roadmapLoading, setRoadmapLoading] = useState(false);
  const [roadmapError, setRoadmapError] = useState<string | null>(null);
  const [roadmapData, setRoadmapData] = useState<Record<string, RoadmapSeries>>({});
  const ganttTopScrollRef = useRef<HTMLDivElement | null>(null);
  const ganttBodyScrollRef = useRef<HTMLDivElement | null>(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const isCoursesView = portalMode === 'coach'
    ? isCoachLearningCoursesPath(location.pathname)
    : isPlayerLearningCoursesPath(location.pathname);
  const isRoadmapView = portalMode === 'coach'
    ? isCoachLearningRoadmapPath(location.pathname)
    : isPlayerLearningRoadmapPath(location.pathname) || new URLSearchParams(location.search).get('view') === 'roadmap';

  useEffect(() => {
    console.log('[LearningMainPage] Component mounted, loading categories...');
    loadSportCategories();
    loadPlayerAge();
  }, []);

  useEffect(() => {
    if (!isRoadmapView) return;
    if (roadmapData[roadmapSport]) {
      // Clear stale error when switching back to a sport that already has loaded data
      setRoadmapError(null);
      return;
    }
    loadRoadmapPoints(roadmapSport);
  }, [isRoadmapView, roadmapSport]);

  const loadSportCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const sports = ['Badminton', 'Cricket', 'Tennis', 'Football', 'Athletics'];
      const categoriesData: SportCategory[] = [];

      // Initialize all sports first (so they show immediately)
      for (const sport of sports) {
        categoriesData.push({ sport, categories: [] });
      }

      // Then load categories for each sport
      for (const sport of sports) {
        try {
          const response = await fetch(`/api/learning-modules/sport/${sport}/categories`);
          const result = await response.json();
          
          if (result.success && result.data && result.data.length > 0) {
            const categoryMap: Record<string, string> = {
              'Forehand Strokes': 'Strokes & Shots',
              'Backhand Strokes': 'Strokes & Shots',
              'Footwork': 'Footwork',
              'Grip and Rotation': 'Techniques',
              'Fitness': 'Fitness',
              'Behavioural skills': 'Drills',
              'General': 'General',
            };

            // Group categories by display name
            const grouped: Record<string, number> = {};
            result.data.forEach((cat: any) => {
              const displayName = categoryMap[cat.category] || cat.category;
              grouped[displayName] = (grouped[displayName] || 0) + cat.skillCount;
            });

            const categories = Object.entries(grouped).map(([displayName, skillCount]) => ({
              category: Object.keys(categoryMap).find(k => categoryMap[k] === displayName) || displayName,
              displayName,
              skillCount: skillCount as number,
            }));

            // Update the sport's categories
            const sportIndex = categoriesData.findIndex(s => s.sport === sport);
            if (sportIndex >= 0) {
              categoriesData[sportIndex].categories = categories;
            }
          }
        } catch (err) {
          console.error(`Error loading categories for ${sport}:`, err);
        }
      }

      setSportCategories(categoriesData);
      console.log('[LearningMainPage] Loaded categories:', categoriesData);
    } catch (err: any) {
      console.error('[LearningMainPage] Error loading sport categories:', err);
      setError('Failed to load learning categories');
    } finally {
      setLoading(false);
    }
  };

  const loadRoadmapPoints = async (sport: string) => {
    try {
      setRoadmapLoading(true);
      setRoadmapError(null);
      const response = await fetch(`/api/admin/ranking-stroke-types?sport=${encodeURIComponent(sport)}`);
      const result = await response.json();
      if (!result?.success || !Array.isArray(result?.data)) {
        setRoadmapData(prev => ({ ...prev, [sport]: { skills: [], drills: [], fitness: [] } }));
        setRoadmapError(`Roadmap data not available for ${sport} yet.`);
        return;
      }

      const rows = result.data as any[];
      const total = rows.length || 1;
      const phaseOf = (month: number): 'L1' | 'L2' | 'L3' | 'L4' =>
        month <= 12 ? 'L1' : month <= 36 ? 'L2' : month <= 54 ? 'L3' : 'L4';
      const weekToMonth = (week: number) =>
        Math.min(ROADMAP_TOTAL_MONTHS, Math.max(1, Math.ceil((week / ROADMAP_TOTAL_WEEKS) * ROADMAP_TOTAL_MONTHS)));
      const fieldValue = (row: any, ...keys: string[]) => {
        for (const key of keys) {
          const value = row?.[key];
          if (value !== undefined && value !== null && String(value).trim() !== '') return String(value);
        }
        return '';
      };
      const distributedMonth = (range: { start: number; end: number }, index: number, count: number) => {
        if (count <= 1) return range.start;
        const span = range.end - range.start;
        return Math.min(range.end, range.start + Math.round((index / (count - 1)) * span));
      };
      const beginnerShotKeywords = ['clear', 'serve', 'keep', 'drop', 'push'];
      const alwaysPracticeTarget = Math.ceil(total * 0.45);
      const skillBands = rows.map((row) => {
        const shotType = fieldValue(row, 'shotType', 'shot_type');
        const strokeType = fieldValue(row, 'strokeType', 'stroke_type');
        const strokeAction = fieldValue(row, 'strokeAction', 'stroke_action');
        const skillText = [shotType, strokeType, strokeAction, row.code].filter(Boolean).join(' ').toLowerCase();
        if (skillText.includes('late') && (skillText.includes('forehand') || skillText.includes('backhand'))) return 'advanced';
        if (beginnerShotKeywords.some((keyword) => skillText.includes(keyword))) return 'beginner';
        return 'intermediate';
      });
      const bandCounts = skillBands.reduce(
        (acc, band) => ({ ...acc, [band]: acc[band] + 1 }),
        { beginner: 0, intermediate: 0, advanced: 0 } as Record<'beginner' | 'intermediate' | 'advanced', number>
      );
      const bandSeen: Record<'beginner' | 'intermediate' | 'advanced', number> = { beginner: 0, intermediate: 0, advanced: 0 };

      const skills: RoadmapPoint[] = rows.map((row, idx) => {
        const difficultyRaw = row?.difficulty;
        const numericDifficulty = Number(difficultyRaw);
        let y = Number.isFinite(numericDifficulty) && numericDifficulty > 0
          ? Math.min(5, Math.max(1, Math.round(numericDifficulty)))
          : 0;
        if (!y) {
          const d = String(difficultyRaw || '').toLowerCase();
          if (d.includes('easy') || d.includes('beginner')) y = 1;
          else if (d.includes('hard') || d.includes('advanced') || d.includes('expert')) y = 5;
          else if (d.includes('medium') || d.includes('intermediate')) y = 3;
          else y = (idx % 5) + 1;
        }

        const shotType = fieldValue(row, 'shotType', 'shot_type');
        const strokeType = fieldValue(row, 'strokeType', 'stroke_type');
        const strokeAction = fieldValue(row, 'strokeAction', 'stroke_action');
        const skillText = [shotType, strokeType, strokeAction, row.code].filter(Boolean).join(' ').toLowerCase();
        const isLateForehandBackhand = skillText.includes('late') && (skillText.includes('forehand') || skillText.includes('backhand'));
        const isBeginnerFundamental = beginnerShotKeywords.some((keyword) => skillText.includes(keyword));
        const isSmash = skillText.includes('smash');
        const isAlwaysPractice = isBeginnerFundamental || idx < alwaysPracticeTarget;
        const skillBand: 'beginner' | 'intermediate' | 'advanced' = isLateForehandBackhand
          ? 'advanced'
          : isBeginnerFundamental
            ? 'beginner'
            : 'intermediate';
        const bandIndex = bandSeen[skillBand]++;
        const startRange = skillBand === 'beginner'
          ? ROADMAP_LEVEL_RANGES.L1
          : skillBand === 'advanced'
            ? { start: ROADMAP_LEVEL_RANGES.L3.start, end: Math.min(ROADMAP_LEVEL_RANGES.L3.end, ROADMAP_LEVEL_RANGES.L3.start + 11), label: ROADMAP_LEVEL_RANGES.L3.label }
            : {
                start: isSmash ? ROADMAP_LEVEL_RANGES.L2.start : ROADMAP_LEVEL_RANGES.L2.start + 2,
                end: ROADMAP_LEVEL_RANGES.L2.end,
                label: ROADMAP_LEVEL_RANGES.L2.label,
              };
        const month = distributedMonth(startRange, bandIndex, bandCounts[skillBand]);
        const week = Math.max(1, Math.round((month / ROADMAP_TOTAL_MONTHS) * ROADMAP_TOTAL_WEEKS));
        const phase = phaseOf(month);
        const endMonth = ROADMAP_TOTAL_MONTHS;

        return {
          month,
          week,
          y,
          phase,
          category: 'Skills',
          code: row.code || `S${idx + 1}`,
          name: shotType || strokeType || row.code || `Skill ${idx + 1}`,
          courtPosition: fieldValue(row, 'courtPosition', 'court_position'),
          halfSide: fieldValue(row, 'halfSide', 'half_side'),
          strokeType,
          strokeAction,
          shotType,
          placement: fieldValue(row, 'placement'),
          startMonth: month,
          endMonth,
          startLevel: phase,
          practiceCadence: isAlwaysPractice ? 'Daily / weekly practice across the roadmap' : 'Practice from start level through Pro',
        };
      });

      // Dry-run drills derived from grouped skills (non-restrictive preview)
      const drillCount = Math.max(40, Math.round(total * 0.35));
      const drills: RoadmapPoint[] = Array.from({ length: drillCount }).map((_, idx) => {
        const week = Math.min(
          ROADMAP_TOTAL_WEEKS,
          Math.max(1, Math.round((idx / Math.max(1, drillCount - 1)) * (ROADMAP_TOTAL_WEEKS - 1)) + 1)
        );
        const month = weekToMonth(week);
        return {
          month,
          week,
          y: ((idx + 1) % 5) + 1,
          phase: phaseOf(month),
          category: 'Drills',
          code: `D${String(idx + 1).padStart(3, '0')}`,
          name: `Drill ${idx + 1}`,
        };
      });

      // Dry-run fitness points distributed monthly/quarterly
      const fitnessCount = 30;
      const fitness: RoadmapPoint[] = Array.from({ length: fitnessCount }).map((_, idx) => {
        const week = Math.min(
          ROADMAP_TOTAL_WEEKS,
          Math.max(1, Math.round((idx / Math.max(1, fitnessCount - 1)) * (ROADMAP_TOTAL_WEEKS - 1)) + 1)
        );
        const month = weekToMonth(week);
        return {
          month,
          week,
          y: (idx % 5) + 1,
          phase: phaseOf(month),
          category: 'Fitness',
          code: `F${String(idx + 1).padStart(3, '0')}`,
          name: `Fitness ${idx + 1}`,
        };
      });

      setRoadmapData(prev => ({ ...prev, [sport]: { skills, drills, fitness } }));
    } catch (e) {
      console.error('Error loading roadmap points:', e);
      setRoadmapError('Unable to load roadmap points');
    } finally {
      setRoadmapLoading(false);
    }
  };

  const loadPlayerAge = async () => {
    try {
      if (!user?.id) return;
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await fetch(`/api/players/me?userId=${user.id}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const result = await response.json();
      if (!result?.success || !result?.data?.dateOfBirth) return;
      const dob = new Date(result.data.dateOfBirth);
      if (Number.isNaN(dob.getTime())) return;
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
      if (age >= 0) setPlayerAge(age);
    } catch {
      // Keep silent in UI; info is shown in tooltip.
    }
  };

  const ageGuidanceText = playerAge === null
    ? 'Age guidance is unavailable because date of birth is missing or invalid. Update DOB in Profile to enable age-based warnings.'
    : playerAge < 10
      ? 'Player age is below 10 years. Keep beginner load practical (usually below 2 hours/day with recovery).'
      : 'For 10+ beginners, progression can be accelerated based on capacity and consistency. Timeline is flexible.';

  const toPlot = (points: RoadmapPoint[]): RoadmapPlotPoint[] => {
    const bandOffset = (seedText: string) => {
      // Stable pseudo-random offset so points in same difficulty band do not stack.
      let hash = 0;
      for (let i = 0; i < seedText.length; i++) {
        hash = (hash * 31 + seedText.charCodeAt(i)) >>> 0;
      }
      const normalized = (hash % 1000) / 1000; // 0..1
      return (normalized - 0.5) * 0.72; // -0.36 .. +0.36 around difficulty center
    };

    return points
      .map((p) => {
        const month = p.month;
        const quarter = Math.ceil(month / 3);
        const week = Number.isFinite(p.week) ? p.week : Math.round((month / ROADMAP_TOTAL_MONTHS) * ROADMAP_TOTAL_WEEKS);
        const x = week;
        const yPlot = Math.max(0.55, Math.min(5.45, p.y + bandOffset(`${p.code}-${p.name}-${week}`)));
        return {
          ...p,
          month,
          week,
          quarter,
          x,
          actionLabel: `${p.code} - ${p.name}`,
          actionLabelShort: `${p.code} - ${String(p.name).slice(0, 46)}${String(p.name).length > 46 ? '...' : ''}`,
          actionLabelWrapped: wrapLabel(`${p.code} - ${p.name}`),
          yPlot,
        };
      })
      .filter((p) => roadmapLevel === 'ALL' || p.phase === roadmapLevel);
  };

  const getRoadmapVisibleKeys = () =>
    roadmapTab === 'ALL' ? (['skills', 'drills', 'fitness'] as const) : ([roadmapTab] as const);

  const getRoadmapLevelMaxMonth = () =>
    roadmapLevel === 'L1' ? 12 : roadmapLevel === 'L2' ? 36 : roadmapLevel === 'L3' ? 54 : ROADMAP_TOTAL_MONTHS;

  const getRoadmapLevelByMonth = (month: number): 'L1' | 'L2' | 'L3' | 'L4' =>
    month <= 12 ? 'L1' : month <= 36 ? 'L2' : month <= 54 ? 'L3' : 'L4';

  const getRoadmapLevelFocus = (month: number) => {
    const level = getRoadmapLevelByMonth(month);
    if (level === 'L1') return 'Beginner: build movement, timing, and basic contact';
    if (level === 'L2') return 'Intermediate: improve repeatability and placement';
    if (level === 'L3') return 'Advanced: improve consistency under pressure';
    return 'Pro: refine accuracy, variation, and match reliability';
  };

  const getRoadmapShotTypeStyle = (point: RoadmapPoint, fallback: { color: string; bg: string }) => {
    const shotType = String(point.shotType || point.name || '').toLowerCase();
    if (shotType.includes('smash') || shotType.includes('kill')) return { color: '#b71c1c', bg: '#ffebee' };
    if (shotType.includes('drop') || shotType.includes('net')) return { color: '#1b5e20', bg: '#e8f5e9' };
    if (shotType.includes('clear') || shotType.includes('lift')) return { color: '#0d47a1', bg: '#e3f2fd' };
    if (shotType.includes('drive') || shotType.includes('push')) return { color: '#e65100', bg: '#fff3e0' };
    if (shotType.includes('serve')) return { color: '#880e4f', bg: '#fce4ec' };
    return fallback;
  };

  const renderRoadmapSkillTooltip = (point: RoadmapPoint, activeMonth?: number) => (
    <Box sx={{ maxWidth: 360 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.5 }}>
        {point.name}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block' }}>
        {[point.courtPosition, point.halfSide, point.strokeType, point.strokeAction, point.shotType, point.placement]
          .filter(Boolean)
          .join(' | ') || 'Skill details unavailable'}
      </Typography>
      {activeMonth && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.9 }}>
          {getRoadmapLevelFocus(activeMonth)}
        </Typography>
      )}
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.85 }}>
        Starts: {ROADMAP_LEVEL_RANGES[point.startLevel || point.phase].label} | {point.practiceCadence || 'Ongoing practice'}
      </Typography>
    </Box>
  );

  const renderRoadmapGanttChart = () => {
    const skillStyle = { color: '#1976d2', bg: '#e3f2fd' };
    const selectedLevelRange = roadmapLevel === 'ALL' ? null : ROADMAP_LEVEL_RANGES[roadmapLevel];
    const levelMaxMonth = getRoadmapLevelMaxMonth();
    const visiblePoints = (roadmapData[roadmapSport]?.skills || [])
      .filter((point) => {
        if (!selectedLevelRange) return true;
        const startMonth = point.startMonth || point.month;
        const endMonth = point.endMonth || ROADMAP_TOTAL_MONTHS;
        return startMonth <= selectedLevelRange.end && endMonth >= selectedLevelRange.start;
      })
      .sort((a, b) => (a.startMonth || a.month) - (b.startMonth || b.month) || a.y - b.y || a.name.localeCompare(b.name));

    if (visiblePoints.length === 0) {
      return <Alert severity="info">No roadmap items available for the selected filters.</Alert>;
    }

    const dataMaxMonth = visiblePoints.reduce(
      (max, point) => Math.max(max, Number(point.endMonth || point.month) || 1),
      1
    );
    const maxMonth = Math.max(levelMaxMonth, dataMaxMonth);
    const months = Array.from({ length: maxMonth }, (_, index) => index + 1);
    const rowHeight = 72;
    const monthWidth = 140;
    const skillColumnWidth = 300;
    const contentWidth = skillColumnWidth + (months.length * monthWidth);
    const rows = visiblePoints;
    const getSkillActivityLabel = (point: RoadmapPoint) => {
      const shotType = String(point.shotType || point.name || 'Skill').trim();
      const placement = String(point.placement || '').trim();
      return {
        primary: shotType,
        secondary: placement && placement.toLowerCase() !== shotType.toLowerCase() ? placement : '',
      };
    };
    const syncGanttScroll = (source: 'top' | 'body') => {
      const top = ganttTopScrollRef.current;
      const body = ganttBodyScrollRef.current;
      if (!top || !body) return;
      if (source === 'top') {
        body.scrollLeft = top.scrollLeft;
      } else {
        top.scrollLeft = body.scrollLeft;
      }
    };

    return (
      <Box sx={{ border: '1px solid #dbe8f6', borderRadius: 1.5, bgcolor: '#fff' }}>
        <Box
          ref={ganttTopScrollRef}
          onScroll={() => syncGanttScroll('top')}
          sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
            borderBottom: '1px solid #dbe8f6',
            bgcolor: '#f8fafc',
            height: 18,
          }}
        >
          <Box sx={{ width: contentWidth, height: 1 }} />
        </Box>
        <Box
          ref={ganttBodyScrollRef}
          onScroll={() => syncGanttScroll('body')}
          sx={{ overflow: 'auto', maxHeight: 720 }}
        >
        <Box sx={{ minWidth: contentWidth, width: 'max-content' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `${skillColumnWidth}px repeat(${months.length}, ${monthWidth}px)`,
              position: 'sticky',
              top: 0,
              zIndex: 3,
              bgcolor: '#f8fafc',
              borderBottom: '2px solid #bfdbfe',
            }}
          >
            <Box sx={{ position: 'sticky', left: 0, zIndex: 4, bgcolor: '#f8fafc', p: 1, fontWeight: 900, color: '#0f4c81', borderRight: '1px solid #dbe8f6' }}>
              Skill / Activity
            </Box>
            {months.map((month) => (
              <Box key={`month-${month}`} sx={{ p: 1, textAlign: 'center', fontWeight: 900, color: '#0f4c81', borderRight: '1px solid #e2e8f0' }}>
                M{month}
              </Box>
            ))}
          </Box>

          {rows.map((point, rowIndex) => {
            const startMonth = point.startMonth || point.month;
            const endMonth = point.endMonth || ROADMAP_TOTAL_MONTHS;
            const skillLabel = getSkillActivityLabel(point);
            return (
              <Box
                key={`skill-${point.code}-${startMonth}-${rowIndex}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `${skillColumnWidth}px repeat(${months.length}, ${monthWidth}px)`,
                  minHeight: rowHeight,
                  borderBottom: '1px solid #eef2f7',
                  bgcolor: rowIndex % 2 === 0 ? '#fff' : '#fbfdff',
                }}
              >
                <Box
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    bgcolor: rowIndex % 2 === 0 ? '#fff' : '#fbfdff',
                    borderRight: '1px solid #dbe8f6',
                    p: 0.9,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 900,
                      color: '#102a43',
                      lineHeight: 1.2,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                  >
                    {skillLabel.primary}
                  </Typography>
                  {skillLabel.secondary && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.25,
                        color: '#475569',
                        fontWeight: 800,
                        lineHeight: 1.2,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      }}
                    >
                      {skillLabel.secondary}
                    </Typography>
                  )}
                </Box>
                {months.map((month) => {
                  const chipStyle = getRoadmapShotTypeStyle(point, skillStyle);
                  const isActiveMonth = month >= startMonth && month <= endMonth;
                  const isLevelStart = [1, 13, 37, 55].includes(month) && month >= startMonth;
                  const showChip = isActiveMonth && (month === startMonth || isLevelStart);
                  const isFirstActiveVisibleMonth = isActiveMonth && (month === startMonth || month === months[0]);
                  const isLastActiveVisibleMonth = isActiveMonth && (month === endMonth || month === months[months.length - 1]);
                  return (
                    <Box
                      key={`${point.category}-${point.name}-m-${month}`}
                      sx={{
                        borderRight: '1px solid #eef2f7',
                        px: 0.4,
                        py: 0.7,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isActiveMonth ? `${chipStyle.bg}44` : 'transparent',
                      }}
                    >
                      {isActiveMonth ? (
                        <MuiTooltip title={renderRoadmapSkillTooltip(point, month)} arrow placement="top">
                          <Box
                            sx={{
                              width: '100%',
                              minHeight: 40,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: chipStyle.bg,
                              borderTop: `1px solid ${chipStyle.color}55`,
                              borderBottom: `1px solid ${chipStyle.color}55`,
                              borderLeft: isFirstActiveVisibleMonth ? `1px solid ${chipStyle.color}55` : 0,
                              borderRight: isLastActiveVisibleMonth ? `1px solid ${chipStyle.color}55` : 0,
                              borderTopLeftRadius: isFirstActiveVisibleMonth ? 999 : 0,
                              borderBottomLeftRadius: isFirstActiveVisibleMonth ? 999 : 0,
                              borderTopRightRadius: isLastActiveVisibleMonth ? 999 : 0,
                              borderBottomRightRadius: isLastActiveVisibleMonth ? 999 : 0,
                              boxShadow: showChip ? `0 6px 14px ${chipStyle.color}22` : 'none',
                            }}
                          >
                            {showChip ? (
                              <Chip
                                label={(
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography component="span" sx={{ display: 'block', fontSize: '0.68rem', fontWeight: 950, lineHeight: 1.08 }}>
                                      {skillLabel.primary}
                                    </Typography>
                                    {skillLabel.secondary && (
                                      <Typography component="span" sx={{ display: 'block', fontSize: '0.62rem', fontWeight: 800, lineHeight: 1.08, opacity: 0.9 }}>
                                        {skillLabel.secondary}
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                                sx={{
                                  height: 42,
                                  maxWidth: monthWidth - 12,
                                  bgcolor: '#fff',
                                  color: chipStyle.color,
                                  border: `1px solid ${chipStyle.color}66`,
                                  fontWeight: 900,
                                  '& .MuiChip-label': {
                                    maxWidth: monthWidth - 28,
                                    overflow: 'hidden',
                                    whiteSpace: 'normal',
                                    textOverflow: 'clip',
                                    px: 0.9,
                                  },
                                }}
                              />
                            ) : null}
                          </Box>
                        </MuiTooltip>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
        </Box>
      </Box>
    );
  };

  const getSportIcon = (sport: string) => {
    switch (sport.toLowerCase()) {
      case 'badminton':
        return <BadmintonIcon sx={{ fontSize: 36, color: '#1976d2' }} />;
      case 'cricket':
        return <CricketIcon sx={{ fontSize: 36, color: '#1976d2' }} />;
      case 'tennis':
        return <TennisIcon sx={{ fontSize: 36, color: '#1976d2' }} />;
      case 'football':
        return <FootballIcon sx={{ fontSize: 36, color: '#1976d2' }} />;
      case 'athletics':
        return <AthleticsIcon sx={{ fontSize: 36, color: '#1976d2' }} />;
      default:
        return <AthleticsIcon sx={{ fontSize: 36, color: '#1976d2' }} />;
    }
  };

  const handleSportClick = (sport: string) => {
    navigate(paths.learningSport(sport));
  };

  const drawer = (
    <Box sx={{ bgcolor: '#2c3e50', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ bgcolor: '#A05AFF', minHeight: '48px !important', gap: 1 }}>
        <EncycdataLogo height={28} lightBackground sx={{ mr: 1 }} />
        <Typography variant="h6" noWrap component="div" sx={{ color: '#ffffff', fontWeight: 700 }}>
          Player Portal
        </Typography>
      </Toolbar>
      <List sx={{ flexGrow: 1 }}>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={() => navigate('/player')}
            sx={{
              color: '#ecf0f1',
              bgcolor: 'transparent',
              '&:hover': { bgcolor: '#34495e' },
            }}
          >
            <ListItemIcon sx={{ color: '#ecf0f1', minWidth: 56 }}>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Dashboard" 
              primaryTypographyProps={{ 
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#ecf0f1'
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={() => navigate('/player?tab=profile')}
            sx={{
              color: '#ecf0f1',
              bgcolor: 'transparent',
              '&:hover': { bgcolor: '#34495e' },
            }}
          >
            <ListItemIcon sx={{ color: '#ecf0f1', minWidth: 56 }}>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Profile" 
              primaryTypographyProps={{ 
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#ecf0f1'
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton 
            selected={isCoursesView}
            onClick={() => navigate('/player/learning/courses')}
            sx={{
              color: '#ecf0f1',
              bgcolor: isCoursesView ? '#34495e' : 'transparent',
              '&:hover': { bgcolor: '#34495e' },
              '&.Mui-selected': { bgcolor: '#34495e', color: '#ff9800', '&:hover': { bgcolor: '#3d566e' } },
            }}
          >
            <ListItemIcon sx={{ color: isCoursesView ? '#ff9800' : '#ecf0f1', minWidth: 56 }}>
              <LearningIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Learning" 
              primaryTypographyProps={{ 
                fontSize: '0.9rem',
                fontWeight: isCoursesView ? 700 : 600,
                color: isCoursesView ? '#ff9800' : '#ecf0f1'
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            selected={isCoursesView}
            onClick={() => navigate('/player/learning/courses')}
            sx={{
              color: '#ecf0f1',
              pl: 7,
              bgcolor: isCoursesView ? '#34495e' : 'transparent',
              '&:hover': { bgcolor: '#34495e' },
              '&.Mui-selected': { bgcolor: '#34495e', color: '#ff9800', '&:hover': { bgcolor: '#3d566e' } },
            }}
          >
            <ListItemIcon sx={{ color: isCoursesView ? '#ff9800' : '#ecf0f1', minWidth: 36 }}>
              <LearningIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Courses"
              primaryTypographyProps={{
                fontSize: '0.85rem',
                fontWeight: isCoursesView ? 700 : 600,
                color: isCoursesView ? '#ff9800' : '#ecf0f1'
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            selected={isRoadmapView}
            onClick={() => navigate('/player/learning/roadmap')}
            sx={{
              color: '#ecf0f1',
              pl: 7,
              bgcolor: isRoadmapView ? '#34495e' : 'transparent',
              '&:hover': { bgcolor: '#34495e' },
              '&.Mui-selected': { bgcolor: '#34495e', color: '#ff9800', '&:hover': { bgcolor: '#3d566e' } },
            }}
          >
            <ListItemIcon sx={{ color: isRoadmapView ? '#ff9800' : '#ecf0f1', minWidth: 36 }}>
              <TimelineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Roadmap"
              primaryTypographyProps={{
                fontSize: '0.85rem',
                fontWeight: isRoadmapView ? 700 : 600,
                color: isRoadmapView ? '#ff9800' : '#ecf0f1'
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={() => navigate('/player/ranking-overview')}
            sx={{
              color: '#ecf0f1',
              bgcolor: 'transparent',
              '&:hover': { bgcolor: '#34495e' },
            }}
          >
            <ListItemIcon sx={{ color: '#ecf0f1', minWidth: 56 }}>
              <RankingIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Learn and Rank" 
              primaryTypographyProps={{ 
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#ecf0f1'
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={() => navigate('/player/performance')}
            sx={{
              color: '#ecf0f1',
              bgcolor: 'transparent',
              '&:hover': { bgcolor: '#34495e' },
            }}
          >
            <ListItemIcon sx={{ color: '#ecf0f1', minWidth: 56 }}>
              <AssessmentIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Training Evaluation" 
              primaryTypographyProps={{ 
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#ecf0f1'
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => navigate('/player/my-reports')}
            sx={{
              color: '#ecf0f1',
              bgcolor: 'transparent',
              '&:hover': { bgcolor: '#34495e' },
            }}
          >
            <ListItemIcon sx={{ color: '#ecf0f1', minWidth: 56 }}>
              <DescriptionIcon />
            </ListItemIcon>
            <ListItemText
              primary="Reports"
              primaryTypographyProps={{
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#ecf0f1'
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => navigate('/player/recommendations')}
            sx={{
              color: '#ecf0f1',
              bgcolor: 'transparent',
              '&:hover': { bgcolor: '#34495e' },
            }}
          >
            <ListItemIcon sx={{ color: '#ecf0f1', minWidth: 56 }}>
              <RecommendationsIcon />
            </ListItemIcon>
            <ListItemText
              primary="AI Assistant"
              primaryTypographyProps={{
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#ecf0f1'
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => navigate('/player/compare-peers')}
            sx={{
              color: '#ecf0f1',
              bgcolor: 'transparent',
              '&:hover': { bgcolor: '#34495e' },
            }}
          >
            <ListItemIcon sx={{ color: '#ecf0f1', minWidth: 56 }}>
              <CompareIcon />
            </ListItemIcon>
            <ListItemText
              primary="Peer Benchmark"
              primaryTypographyProps={{
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#ecf0f1'
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={() => navigate('/player?tab=certificates')}
            sx={{
              color: '#ecf0f1',
              bgcolor: 'transparent',
              '&:hover': { bgcolor: '#34495e' },
            }}
          >
            <ListItemIcon sx={{ color: '#ecf0f1', minWidth: 56 }}>
              <CertificateIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Certificates" 
              primaryTypographyProps={{ 
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#ecf0f1'
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
      <Box sx={{ mt: 'auto', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleLogout}
            sx={{
              color: '#ff9800',
              '&:hover': {
                bgcolor: 'rgba(255, 152, 0, 0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ color: '#ff9800', minWidth: 56 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Logout"
              primaryTypographyProps={{ 
                fontSize: '0.9rem', 
                fontWeight: 600,
                color: '#ff9800'
              }}
            />
          </ListItemButton>
        </ListItem>
      </Box>
    </Box>
  );

  const mainWidthSx = hideSidebar ? '100%' : `calc(100% - ${DRAWER_WIDTH}px)`;

  if (loading) {
    return (
      <Box sx={{ display: 'flex' }}>
        {!hideSidebar && (
        <Box
          component="nav"
          sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
        >
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
        )}
        <Box
          component="main"
          sx={{ 
            flexGrow: 1, 
            p: 3, 
            width: { sm: mainWidthSx },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px'
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex' }}>
        {!hideSidebar && (
        <Box
          component="nav"
          sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
        >
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
        )}
        <Box
          component="main"
          sx={{ 
            flexGrow: 1, 
            p: 3, 
            width: { sm: mainWidthSx }
          }}
        >
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Button onClick={() => loadSportCategories()} variant="contained">
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      {!hideSidebar && (
      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          width: { sm: mainWidthSx }
        }}
      >
        <Container maxWidth="lg" sx={{ mb: 4 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LearningIcon sx={{ fontSize: 32, color: '#1976d2' }} />
              {isRoadmapView ? 'Learning Roadmap' : 'Learning Courses'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {isRoadmapView
                ? 'Roadmap view is under Learning. Select a sport to open roadmap-linked learning categories.'
                : 'Select a sport to start learning skills through videos, explanations, and practice steps.'}
            </Typography>
          </Box>

          {isRoadmapView && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Sport</InputLabel>
                  <Select
                    value={roadmapSport}
                    label="Sport"
                    onChange={(e) => setRoadmapSport(String(e.target.value))}
                  >
                    {sportCategories.map((s) => (
                      <MenuItem key={s.sport} value={s.sport}>{s.sport}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Level</InputLabel>
                  <Select
                    value={roadmapLevel}
                    label="Level"
                    onChange={(e) => setRoadmapLevel(e.target.value as 'ALL' | 'L1' | 'L2' | 'L3' | 'L4')}
                  >
                    <MenuItem value="ALL">All Levels</MenuItem>
                    <MenuItem value="L1">L1 - Beginner</MenuItem>
                    <MenuItem value="L2">L2 - Intermediate</MenuItem>
                    <MenuItem value="L3">L3 - Advanced</MenuItem>
                    <MenuItem value="L4">L4 - Pro</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 190 }}>
                  <InputLabel>Chart Type</InputLabel>
                  <Select
                    value={roadmapChartType}
                    label="Chart Type"
                    onChange={(e) => setRoadmapChartType(e.target.value as 'scatter' | 'gantt')}
                  >
                    <MenuItem value="scatter">Timeline Scatter</MenuItem>
                    <MenuItem value="gantt">Gantt Chart</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: { xs: 0, sm: 'auto' } }}>
                  <MuiTooltip
                    title={roadmapChartType === 'gantt'
                      ? 'Gantt chart uses skills/activities on Y-axis and months on X-axis, like a product roadmap.'
                      : 'Timeline Scatter uses timeline on X-axis and difficulty on Y-axis. Scroll horizontally to read stroke/shot labels.'}
                    arrow
                    placement="top"
                  >
                    <IconButton size="small" color="primary" aria-label="Roadmap help">
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip
                    title={ageGuidanceText}
                    arrow
                    placement="top"
                  >
                    <IconButton size="small" color={playerAge !== null && playerAge < 10 ? 'warning' : 'default'} aria-label="Age guidance">
                      <WarningIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip
                    title="Recommended training load guidance: L1 ~1-3h/day, L2 ~2-4h/day, L3 ~3-6h/day, L4 ~4-8h/day (sport and recovery dependent)."
                    arrow
                    placement="top"
                  >
                    <IconButton size="small" color="success" aria-label="Training load tips">
                      <TipIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                </Box>
              </Box>

              {roadmapLoading ? (
                <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
              ) : roadmapError && !(roadmapData[roadmapSport]?.skills?.length || roadmapData[roadmapSport]?.drills?.length || roadmapData[roadmapSport]?.fitness?.length) ? (
                <Alert severity="warning">{roadmapError}</Alert>
              ) : (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Tabs
                    value={roadmapTab}
                    onChange={(_, value) => setRoadmapTab(value)}
                    sx={{ mb: 1.5 }}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab label="All" value="ALL" />
                    <Tab label="Skills" value="skills" />
                    <Tab label="Drills" value="drills" />
                    <Tab label="Fitness" value="fitness" />
                  </Tabs>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    {roadmapChartType === 'gantt' ? 'Gantt Roadmap' : 'Timeline Scatter Roadmap'} {roadmapLevel !== 'ALL' ? `(${roadmapLevel})` : '(L1-L4)'}
                  </Typography>
                  {roadmapChartType === 'gantt' ? renderRoadmapGanttChart() : (() => {
                    const series = {
                      skills: { name: 'Skills', color: '#1976d2', data: toPlot(roadmapData[roadmapSport]?.skills || []) },
                      drills: { name: 'Drills', color: '#ff9800', data: toPlot(roadmapData[roadmapSport]?.drills || []) },
                      fitness: { name: 'Fitness', color: '#4caf50', data: toPlot(roadmapData[roadmapSport]?.fitness || []) },
                    };
                    const visibleKeys = getRoadmapVisibleKeys();
                    const merged = visibleKeys.flatMap((k) => series[k].data);
                    const infoHeight = 860;
                    const levelMaxX = roadmapLevel === 'L1' ? 78 : roadmapLevel === 'L2' ? 156 : roadmapLevel === 'L3' ? 234 : 260;
                    const dataMaxX = merged.reduce((max, p) => Math.max(max, Number(p.x) || 1), 1);
                    const xDomainMax = roadmapLevel === 'ALL' ? Math.max(levelMaxX, dataMaxX) : levelMaxX;
                    const weekStep = xDomainMax <= 90 ? 1 : xDomainMax <= 180 ? 2 : 3;
                    const xTicks = Array.from(
                      { length: Math.floor((xDomainMax - 1) / weekStep) + 1 },
                      (_, i) => 1 + (i * weekStep)
                    );
                    const chartMinWidth = Math.max(3200, xDomainMax * 38);

                    return (
                      <Box
                        sx={{
                          height: infoHeight,
                          overflowY: 'hidden',
                          border: '1px solid #f0f0f0',
                          borderRadius: 1,
                          p: 1,
                          overflowX: 'auto',
                        }}
                      >
                        <Box sx={{ minWidth: chartMinWidth, height: '100%' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <ScatterChart
                              margin={{
                                top: 10,
                                right: 420,
                                bottom: 20,
                                left: 16,
                              }}
                            >
                              <CartesianGrid />
                              <XAxis
                                type="number"
                                dataKey="x"
                                domain={[1, xDomainMax]}
                                ticks={xTicks}
                                interval={0}
                                tickFormatter={(v) => `W${v}`}
                              />
                              <YAxis
                                type="number"
                                dataKey="yPlot"
                                domain={[0.5, 5.5]}
                                ticks={[1, 2, 3, 4, 5]}
                                tickFormatter={(v) => DIFFICULTY_TEXT[Number(v)] || String(v)}
                                width={96}
                              />
                              <Tooltip
                                labelFormatter={(_, payload: any) => {
                                  const p = payload?.[0]?.payload;
                                  return p ? `${p.code} - ${p.name} (${p.phase}) | W${p.week} / M${p.month}` : '';
                                }}
                              />
                              <Legend />
                              {visibleKeys.map((key) => (
                                <Scatter key={key} name={series[key].name} data={series[key].data} fill={series[key].color} />
                              ))}
                              <Scatter name="Code + Stroke/Shot" data={merged} fill="transparent">
                                <LabelList
                                  dataKey="actionLabelWrapped"
                                  position="right"
                                  fontSize={9}
                                  offset={8}
                                  content={(props: any) => {
                                    const { x, y, value } = props || {};
                                    if (typeof value !== 'string') return null;
                                    const lines = value.split('\n').slice(0, 3);
                                    return (
                                      <text x={x + 8} y={y - 10} fontSize={9} fill="#374151">
                                        {lines.map((line, idx) => (
                                          <tspan key={`${line}-${idx}`} x={x + 8} dy={idx === 0 ? 0 : 10}>
                                            {line}
                                          </tspan>
                                        ))}
                                      </text>
                                    );
                                  }}
                                />
                              </Scatter>
                            </ScatterChart>
                          </ResponsiveContainer>
                        </Box>
                      </Box>
                    );
                  })()}
                </Paper>
              )}
            </Paper>
          )}

          {!isRoadmapView && (
            <Grid container spacing={2}>
              {sportCategories.map((sportData) => (
                <Grid item xs={6} sm={4} md={3} key={sportData.sport}>
                  <Card
                    sx={{
                      height: '100%',
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-4px)',
                        transition: 'all 0.3s ease',
                      },
                    }}
                  >
                    <CardActionArea onClick={() => handleSportClick(sportData.sport)}>
                      <CardContent sx={{ p: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          {getSportIcon(sportData.sport)}
                          <Typography variant="h6" sx={{ ml: 1.5, fontWeight: 600 }}>
                            {sportData.sport}
                          </Typography>
                          <ArrowForwardIcon sx={{ ml: 'auto', color: '#1976d2' }} />
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Open courses
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default LearningMainPage;

